import { useNavigate, useLocation } from "@tanstack/react-router"
import { useRef } from "react"
import { APP_ROUTES } from "@/constants/routes"
import { Route } from "@/types/route"

export enum RouterEvent {
  routeChangeStart = "routeChangeStart",
  routeChangeComplete = "routeChangeComplete",
}

export class Router {
  private navigate: ReturnType<typeof useNavigate>
  private location: ReturnType<typeof useLocation>
  private eventListeners: Map<string, Set<(url?: string, route?: Route) => boolean | void>> =
    new Map()
  private userRoutes: Route[] = []

  constructor(
    navigate: ReturnType<typeof useNavigate>,
    location: ReturnType<typeof useLocation>,
    routes: Route[] = [],
  ) {
    this.navigate = navigate
    this.location = location
    this.userRoutes = routes
  }

  push(path: string) {
    this.navigate({ to: path })
  }

  replace(path: string) {
    this.navigate({ to: path, replace: true })
  }

  back() {
    window.history.back()
  }

  forward() {
    window.history.forward()
  }

  addEventListener(event: string, cb: (url?: string, route?: Route) => boolean | void) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set())
    }
    this.eventListeners.get(event)?.add(cb)
  }

  removeEventListener(event: RouterEvent, cb: (url?: string, route?: Route) => boolean | void) {
    this.eventListeners.get(event)?.delete(cb)
  }

  getRoutes() {
    return this.userRoutes
  }

  setRoutes(routes: Route[]) {
    this.userRoutes = routes
  }

  findRoute(path: string): Route | undefined {
    // 경로에 해당하는 route와 그 부모들을 찾아서 계층 구조로 반환
    const findRouteWithParents = (
      routes: Route[],
      targetPath: string,
      parent?: Route,
    ): Route | undefined => {
      for (const route of routes) {
        if (route.path === targetPath) {
          // 찾은 route를 반환하되, 부모가 있으면 부모의 children으로 설정
          return parent ? { ...parent, children: [route] } : route
        }

        if (route.children) {
          // 자식 경로에서 찾기
          const found = findRouteWithParents(route.children, targetPath, route)
          if (found) {
            // 부모가 있으면 부모의 children으로 found를 설정
            return parent ? { ...parent, children: [found] } : found
          }
        }
      }
      return undefined
    }

    return findRouteWithParents(this.userRoutes, path)
  }

  callChangeEvent(pathName: string) {
    // 라우트 변경 이벤트 호출
    const route = this.findRoute(pathName)
    const listeners = this.eventListeners.get(RouterEvent.routeChangeStart)
    if (listeners) {
      listeners.forEach((cb) => cb(pathName, route))
    }
  }

  callChangeCompleteEvent(pathName: string) {
    // 라우트 변경 완료 이벤트 호출
    const route = this.findRoute(pathName)
    const listeners = this.eventListeners.get(RouterEvent.routeChangeComplete)
    if (listeners) {
      listeners.forEach((cb) => cb(pathName, route))
    }
  }

  routeChangeCompleteEventsLength() {
    return this.eventListeners.get(RouterEvent.routeChangeComplete)?.size || 0
  }
}

export default function useRouter() {
  const navigate = useNavigate()
  const location = useLocation()
  const routerRef = useRef<Router | null>(null)

  if (!routerRef.current) {
    routerRef.current = new Router(navigate, location, APP_ROUTES)
  } else {
    // Update internal references
    routerRef.current["navigate"] = navigate
    routerRef.current["location"] = location
  }

  return routerRef.current
}
